/**
@format
@flow local-strict
*/

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import ActionTypes from '@ziynx/action-types';
import styled from 'styled-components';
import { eventNames } from '@ziynx/analytics';
import { Helmet } from 'react-helmet-async';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import BannerNotification from '@ziynx/components/notifications';
import Input from '@ziynx/components/inputs';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import { withAppManager, nameInputsRegex } from '@ziynx/components/utilities';
import type { Viewports } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type Props = $ReadOnly<{|
    /**
     * Navigation stack
     */
    history: Object,
    /**
     * Viewports
     */
    viewport: Viewports
|}>;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px' : '40px')};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall ? 'margin-bottom: 16px;' : 'margin-right: 16px;')}
  }
`;

const StyledButtonSection = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const StyledFormRow = styled(FormRow)`
  ${({ firstChild }) => firstChild && 'padding-top: 56px;'}
  ${({ isMobileSmall, firstChild }) => firstChild && isMobileSmall && 'padding-top: 52px;'}
`;

const StyledBannerWrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 996;
  width: 100%;
  flex: 1;
`;

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

const INPUT_FIELDS = [
  'firstName',
  'lastName',
];

const EditPersonalInfoScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;
  const isMobileSmall = viewport === 'mobileSmall';
  const {
    handleSubmit,
    control,
    errors,
    clearErrors,
    formState: { dirtyFields },
  } = useForm({ mode: 'onTouched' });

  const userInfoService = useSelector(({ user }) => user.GET_USER_INFO_API ?? {}, shallowEqual);
  const userPatchService = useSelector(({ user }) => user.PATCH_USER_INFO_API ?? {}, shallowEqual);

  const dispatch = useDispatch();

  // When there's an error with updating
  const [hasError, setHasError] = useState(false);
  // state for banner error message
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // Shows if waiting for response
  const [isLoading, setIsLoading] = useState(false);
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);

  /**
   * Function to signal there's an error
   * and render banner
   */
  const setBannerError = (): void => {
    setHasError(true);
    setRenderBanner(true);
  };

  // /**
  //  * Function to reset any banner errors,
  //  * and un-render banner, delay 6000ms
  //  */
  const resetBannerRender = (): void => {
    setTimeout(() => {
      setHasError(false);
      setRenderBanner(false);
    }, bannerTimeOut + 1000);
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.user.getInfo.START,
    });
  }, []);

  useEffect(() => {
    if (userPatchService?.patching) {
      setIsLoading(true);
    } else if (userPatchService?.success) {
      dispatch({
        type: ActionTypes.user.clearPatch,
      });
      history.push('/settings', { from: 'Edit Personal Information', userInfoUpdated: true });
    } else if (userPatchService?.error) {
      setIsLoading(false);
      setBannerError();
      setBannerErrorMessage('Failed to update. Please try again later.');
      resetBannerRender();
      setTimeout(() => {
        dispatch({
          type: ActionTypes.user.clearPatch,
        });
      }, 2000);
    }
  }, [userPatchService?.patching]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_edit_personal_information,
    });
  }, []);

  const goBack = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.exited_edit_personal_information,
    });
    history.goBack();
  };

  const _getPatchOps = ({ data }) => {
    const ops = [];

    INPUT_FIELDS.forEach((field) => {
      if (!dirtyFields[field]) return;
      if (field === 'firstName' && data?.firstName) {
        ops.push(
          {
            op: 'replace',
            path: 'firstName',
            value: data.firstName?.trim(),
          },
        );
      } else if (field === 'lastName' && data?.lastName) {
        ops.push(
          {
            op: 'replace',
            path: 'lastName',
            value: data.lastName?.trim(),
          },
        );
      }
    });

    return ops;
  };

  const onSubmit = async (
    data: { firstName: string, lastName: string },
  ): Promise<any> => {
    const patchOps = _getPatchOps({ data });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.saved_new_personal_information,
    });
    dispatch({
      type: ActionTypes.user.patch.START,
      body: patchOps,
    });
  };

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!dirtyFields || isLoading) return true;
    return Object.keys(dirtyFields).length === 0;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => Object.keys(errors).length > 0;

  /**
   * Pressing Enter/Return key to submit within Controller
   */
  const onKeyUp = (event): void => {
    if (event?.keyCode === 13) {
      handleSubmit(onSubmit)();
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit Personal Information | ZIYNX</title>
      </Helmet>
      {shouldRenderBanner && (
        <StyledBannerWrapper>
          <BannerNotification
            animated
            showCloseIcon
            timeout={bannerTimeOut}
            variant={hasError ? 'error' : 'success'}
          >
            {hasError && bannerErrorMessage}
          </BannerNotification>
        </StyledBannerWrapper>
      )}
      <ContentContainer>
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper isMobileSmall={isMobileSmall}>
            <Form
              formID="edit-personal-info"
              title="Edit personal information"
              width="100%"
              headingPaddingBottom={isMobileSmall ? '40px' : '56px'}
            >
              <StyledFormRow>
                <Controller
                  control={control}
                  render={({
                    onFocus, onChange, onBlur, value,
                  }) => {
                    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    };

                    const onInputFocus = (): any => {
                      if (errors?.firstName) clearErrors('firstName');
                      return onFocus;
                    };
                    return (
                      <Input
                        variant="text"
                        error={!!errors?.firstName}
                        errorLabel={errors?.firstName?.message}
                        inputName="firstName"
                        required
                        label="First name"
                        onBlur={onBlur}
                        onChange={onInputChange}
                        onFocus={onInputFocus}
                        onKeyUp={onKeyUp}
                        value={value}
                      />
                    );
                  }}
                  name="firstName"
                  defaultValue={userInfoService?.firstName || ' '}
                  rules={{
                    required: 'Please enter a first name',
                    pattern: {
                      value: nameInputsRegex,
                      message: 'Please enter a valid first name',
                    },
                  }}
                />
              </StyledFormRow>
              <StyledFormRow>
                <Controller
                  control={control}
                  render={({
                    onFocus, onChange, onBlur, value,
                  }) => {
                    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
                      (event?.target: HTMLInputElement);
                      onChange(event);
                    };
                    const onInputFocus = (): any => {
                      if (errors.lastName) clearErrors('lastName');
                      return onFocus;
                    };
                    return (
                      <Input
                        variant="text"
                        error={!!errors?.lastName}
                        errorLabel={errors?.lastName?.message}
                        inputName="lastName"
                        required
                        onChange={onInputChange}
                        onFocus={onInputFocus}
                        onBlur={onBlur}
                        onKeyUp={onKeyUp}
                        label="Last name"
                        value={value}
                      />
                    );
                  }}
                  name="lastName"
                  defaultValue={userInfoService?.lastName || ' '}
                  rules={{
                    required: 'Please enter a last name',
                    pattern: {
                      value: nameInputsRegex,
                      message: 'Please enter a valid last name',
                    },
                  }}
                />
              </StyledFormRow>
              <StyledButtonSection isMobileSmall={isMobileSmall}>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  disabled={isDisabled() || isError()}
                  isLoading={isLoading}
                  width="50%"
                  form="change-password"
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </StyledButton>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  inverted
                  width="50%"
                  backgroundColor={Colors.White}
                  onClick={goBack}
                >
                  Cancel
                </StyledButton>
              </StyledButtonSection>
            </Form>
          </PageContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(EditPersonalInfoScreen): React$ComponentType<ManagedProps<Props>>);
